import React, { useEffect, useState } from "react";
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import { Language, PortfolioContent } from '../data/content';

const drawerWidth = 260;

interface NavigationProps {
  lang: Language;
  onLangChange: (lang: Language) => void;
  content: PortfolioContent['nav'];
}

function Navigation({ lang, onLangChange, content }: NavigationProps) {
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);

  const navItems = [
    { label: content.about, id: 'about' },
    { label: content.expertise, id: 'expertise' },
    { label: content.history, id: 'history' },
    { label: content.projects, id: 'projects' },
    { label: content.contact, id: 'contact' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById("navigation");
      if (navbar) {
        const isScrolled = window.scrollY > 20;
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (section: string) => {
    if (section === 'about') {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      return;
    }
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const drawer = (
    <Box className="navigation-bar-responsive" onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <p className="mobile-menu-top">Menu</p>
      <Divider sx={{ my: 1 }} />
      <div className="mobile-lang-box" onClick={(e) => e.stopPropagation()}>
        <div className="lang-switch">
          <button
            type="button"
            className={lang === 'pl' ? 'active' : ''}
            onClick={() => onLangChange('pl')}
          >
            PL
          </button>
          <button
            type="button"
            className={lang === 'en' ? 'active' : ''}
            onClick={() => onLangChange('en')}
          >
            EN
          </button>
        </div>
      </div>
      <Divider sx={{ my: 1 }} />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton sx={{ textAlign: 'center' }} onClick={() => scrollToSection(item.id)}>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href={content.cvUrl}
            target="_blank"
            rel="noreferrer"
            sx={{ textAlign: 'center', fontWeight: 600 }}
          >
            <ListItemText primary={content.downloadCv} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar component="nav" id="navigation" className={`navbar-fixed-top${scrolled ? ' scrolled' : ''}`}>
        <Toolbar className='navigation-bar'>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <span
              className="nav-logo"
              style={{ cursor: 'pointer' }}
              onClick={() => scrollToSection('about')}
            >
              Paweł Ryś
            </span>
            <div className="lang-switch">
              <button
                type="button"
                className={lang === 'pl' ? 'active' : ''}
                onClick={() => onLangChange('pl')}
                aria-label="Wybierz język polski"
              >
                PL
              </button>
              <button
                type="button"
                className={lang === 'en' ? 'active' : ''}
                onClick={() => onLangChange('en')}
                aria-label="Select English language"
              >
                EN
              </button>
            </div>
          </Box>

          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
            {navItems.map((item) => (
              <Button key={item.id} onClick={() => scrollToSection(item.id)}>
                {item.label}
              </Button>
            ))}
            <Button
              component="a"
              href={content.cvUrl}
              target="_blank"
              rel="noreferrer"
              sx={{
                border: '1px solid #2B2B2B',
                borderRadius: '4px',
                ml: 1,
                fontWeight: 600,
              }}
            >
              {content.downloadCv}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
}

export default Navigation;